import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { color } from '../../../app/theme';
import { PlatformType } from '../../../app/types';
import IconCardConfirm from '../../../assets/images/Icons/IconCardConfirm';
import IconCardItem from '../../../assets/images/Icons/IconCardItem';
import IconCarousel from '../../../assets/images/Icons/IconCarousel';
import IconFile from '../../../assets/images/Icons/IconFile';
import IconImagemap from '../../../assets/images/Icons/IconImagemap';
import IconText from '../../../assets/images/Icons/IconText';
import IconWaList from '../../../assets/images/Icons/IconWaList';
import IconWhatsappTemplate from '../../../assets/images/Icons/IconWhatsappTemplate';
import ChevronUpIcon from '../../../assets/images/svg/ic-chevron-up.svg';

const useStyles = makeStyles(
  () => ({
    root: {
      cursor: 'pointer',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      fontSize: 15,
      color: color.$13,
      width: 224,
      height: 44,
      borderRadius: 22,
      border: 'solid 1px #bdbdbd',
      backgroundColor: '#ffffff',
      padding: '0 46px 0 24px',
    },
    icon: {
      position: 'absolute',
      top: '50%',
      right: 16,
      transform: 'translateY(-50%) rotate(-180deg)',
      transition: 'transform .2s linear',
    },
    'icon--up': {
      transform: 'translateY(-50%)',
    },
    'template-menu': {
      position: 'absolute',
      zIndex: 1,
      top: 48,
      left: 0,
      right: 0,
      minWidth: 228,
      maxHeight: 0,
      transition: 'max-height .2s linear',
      boxShadow:
        '0 16px 48px 0 rgba(0, 0, 0, 0.07), 0 6px 18px 0 rgba(0, 0, 0, 0.03)',
      backgroundColor: '#ffffff',
      borderRadius: 12,
    },
    'template-menu--expand': {
      maxHeight: 280,
      padding: '8px 0 8px',
    },
    'template-menu-item': {
      display: 'flex',
      alignItems: 'center',
      height: 44,
      fontSize: 13,
      padding: '0 0 0 24px',
      '&:hover': {
        color: color.$1,
      },
    },
    'template-menu-icon': {
      margin: '0 8px 0 0',
    },
    logo: {
      display: 'block',
      margin: '0 0 0 4px',
      width: 16,
    },
  }),
  {
    name: 'TemplateDropdown',
  },
);

function TemplateDropdown({
  platformType,
  messageTemplatetype,
  onChange,
  className,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isExpand, setIsExpand] = useState(false);
  const [mainColor, setMainColor] = useState(color.$10);
  const [hoverIndex, setHoverIndex] = useState(0);

  return (
    <ClickAwayListener onClickAway={() => setIsExpand(false)}>
      <div
        className={classnames(classes.root, className)}
        onClick={() => setIsExpand(!isExpand)}
      >
        {t(`types.message-template-type.${messageTemplatetype}`)}
        <img
          src={ChevronUpIcon}
          className={classnames(classes.icon, {
            [classes['icon--up']]: isExpand,
          })}
          alt="expand-icon"
        />
        <div
          className={classnames(classes['template-menu'], {
            [classes['template-menu--expand']]: isExpand,
          })}
        >
          {isExpand && (
            <>
              <div
                className={classes['template-menu-item']}
                onClick={() => onChange('text')}
                onMouseEnter={() => {
                  setMainColor(color.$1);
                  setHoverIndex(1);
                }}
                onMouseLeave={() => {
                  setMainColor(color.$10);
                  setHoverIndex(0);
                }}
              >
                <IconText
                  id="text-icon"
                  className={classes['template-menu-icon']}
                  force
                  color={hoverIndex === 1 ? mainColor : color.$10}
                />
                {t('templates.type.text')}
              </div>
              <div
                className={classes['template-menu-item']}
                onClick={() => onChange('file')}
                onMouseEnter={() => {
                  setMainColor(color.$1);
                  setHoverIndex(2);
                }}
                onMouseLeave={() => {
                  setMainColor(color.$10);
                  setHoverIndex(0);
                }}
              >
                <IconFile
                  id="file-icon"
                  className={classes['template-menu-icon']}
                  force
                  iconColor={hoverIndex === 2 ? mainColor : color.$10}
                />
                {t('templates.type.file')}
              </div>
              <div
                className={classes['template-menu-item']}
                onClick={() => onChange('card')}
                onMouseEnter={() => {
                  setMainColor(color.$1);
                  setHoverIndex(3);
                }}
                onMouseLeave={() => {
                  setMainColor(color.$10);
                  setHoverIndex(0);
                }}
              >
                <IconCardItem
                  id="card-icon"
                  className={classes['template-menu-icon']}
                  force
                  color={hoverIndex === 3 ? mainColor : color.$10}
                />
                {t('templates.type.card')}
              </div>
              <div
                className={classes['template-menu-item']}
                onClick={() => onChange('confirm')}
                onMouseEnter={() => {
                  setMainColor(color.$1);
                  setHoverIndex(4);
                }}
                onMouseLeave={() => {
                  setMainColor(color.$10);
                  setHoverIndex(0);
                }}
              >
                <IconCardConfirm
                  id="confirm-icon"
                  className={classes['template-menu-icon']}
                  force
                  color={hoverIndex === 4 ? mainColor : color.$10}
                />
                {t('templates.type.confirm')}
              </div>
              {platformType === PlatformType.LINE && (
                <div
                  className={classes['template-menu-item']}
                  onClick={() => onChange('imagemap')}
                  onMouseEnter={() => {
                    setMainColor(color.$1);
                    setHoverIndex(5);
                  }}
                  onMouseLeave={() => {
                    setMainColor(color.$10);
                    setHoverIndex(0);
                  }}
                >
                  <IconImagemap
                    id="imagemap-icon"
                    className={classes['template-menu-icon']}
                    force
                    color={hoverIndex === 5 ? mainColor : color.$10}
                  />
                  {t('templates.type.imagemap')}
                </div>
              )}
              {platformType === PlatformType.LINE && (
                <div
                  className={classes['template-menu-item']}
                  onClick={() => onChange('image')}
                  onMouseEnter={() => {
                    setMainColor(color.$1);
                    setHoverIndex(6);
                  }}
                  onMouseLeave={() => {
                    setMainColor(color.$10);
                    setHoverIndex(0);
                  }}
                >
                  <IconCarousel
                    id="image-icon"
                    className={classes['template-menu-icon']}
                    force
                    color={hoverIndex === 6 ? mainColor : color.$10}
                  />
                  {t('templates.type.image')}
                </div>
              )}
              {platformType === PlatformType.WHATSAPP && (
                <div
                  className={classes['template-menu-item']}
                  onClick={() => onChange('whatsappTemplate')}
                  onMouseEnter={() => {
                    setMainColor(color.$1);
                    setHoverIndex(7);
                  }}
                  onMouseLeave={() => {
                    setMainColor(color.$10);
                    setHoverIndex(7);
                  }}
                >
                  <IconWhatsappTemplate
                    id="image-icon"
                    className={classes['template-menu-icon']}
                    color={hoverIndex === 7 ? mainColor : color.$10}
                    hoverColor={hoverIndex === 7 ? mainColor : color.$10}
                  />
                  {t('templates.type.whatsappTemplate')}
                </div>
              )}
              {platformType === PlatformType.WHATSAPP && (
                <div
                  className={classes['template-menu-item']}
                  onClick={() => onChange('waList')}
                  onMouseEnter={() => {
                    setMainColor(color.$1);
                    setHoverIndex(8);
                  }}
                  onMouseLeave={() => {
                    setMainColor(color.$10);
                    setHoverIndex(8);
                  }}
                >
                  <IconWaList
                    id="waList-icon"
                    className={classes['template-menu-icon']}
                    color={hoverIndex === 7 ? mainColor : color.$10}
                    hoverColor={hoverIndex === 7 ? mainColor : color.$10}
                  />
                  {t('templates.type.waList')}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </ClickAwayListener>
  );
}

TemplateDropdown.defaultProps = {
  onChange: () => {},
  className: undefined,
};

TemplateDropdown.propTypes = {
  className: PropTypes.string,
  onChange: PropTypes.func,
};
export default TemplateDropdown;
